import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Phase 26.9 — Turn an authenticated cover URL into a blob: URL that
 * <img> can load without needing a Bearer token in the request.
 *
 * Behaviour:
 *   * Returns `null` while loading or if the cover 404s (caller shows
 *     the placeholder).
 *   * Fetches with the standard axios interceptor (which attaches the
 *     Authorization header).
 *   * Revokes the previous blob URL when a new one is issued to avoid
 *     leaking blob memory.
 *
 * Usage:
 *   const url = useInternalCoverBlob(event.cover_image_url);
 *   <EventCover coverUrl={url} title={...} />
 */
export default function useInternalCoverBlob(relativeUrl) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!relativeUrl) { setBlobUrl(null); return undefined; }
    let cancelled = false;
    let currentBlobUrl = null;

    const token = localStorage.getItem('token');
    const backend = process.env.REACT_APP_BACKEND_URL;
    // If the URL is a public path we can just render it directly.
    if (relativeUrl.startsWith('/api/public/')) {
      setBlobUrl(`${backend}${relativeUrl}`);
      return () => { cancelled = true; };
    }

    axios
      .get(`${backend}${relativeUrl}`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (cancelled) return;
        currentBlobUrl = URL.createObjectURL(res.data);
        setBlobUrl(currentBlobUrl);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      });

    return () => {
      cancelled = true;
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [relativeUrl]);

  return blobUrl;
}
